/**
 * useItems Hook - React hook for fetching items by itemSetId
 *
 * @param {string} itemSetId - Item set ID to fetch items for
 * @param {Array} fallbackItems - Fallback items array (for backward compatibility)
 * @returns {Object} { items, loading, error, refetch }
 *
 * @example
 * const { items, loading, error, refetch } = useItems(quote.itemSetId, quote.items || []);
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { ItemSetService } from "../shared/services/itemSetService.js";
import { logError } from "../shared/utils/logger.js";

export const useItems = (itemSetId, fallbackItems = []) => {
  const [items, setItems] = useState(fallbackItems);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force refetch

  // Use ref to store fallbackItems to avoid re-fetching when it changes
  const fallbackRef = useRef(fallbackItems);

  // Update ref when fallbackItems changes (but don't trigger re-fetch)
  useEffect(() => {
    fallbackRef.current = fallbackItems;
  }, [fallbackItems]);

  // Refetch function that can be called manually
  const refetch = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!itemSetId) {
      // No itemSetId provided, use fallback items
      setItems(fallbackRef.current);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const itemsData = await ItemSetService.getItemsBySetId(itemSetId);
        setItems(itemsData.length > 0 ? itemsData : fallbackRef.current);
      } catch (err) {
        logError("useItems: Failed to fetch items", {
          itemSetId,
          error: err.message,
        });
        setError(err.message);
        setItems(fallbackRef.current); // Fallback to provided items
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, [itemSetId, refreshKey]); // Add refreshKey to dependencies to trigger refetch

  return { items, loading, error, refetch };
};
